import {Component, DestroyRef, EventEmitter, inject, Input, OnInit, Output, ChangeDetectionStrategy} from '@angular/core';
import {takeUntilDestroyed} from '@angular/core/rxjs-interop';
import {FormsModule} from '@angular/forms';
import {FaIconComponent} from '@fortawesome/angular-fontawesome';
import {faTrash} from '@fortawesome/pro-solid-svg-icons';
import {Subject} from 'rxjs';
import {debounceTime, switchMap} from 'rxjs/operators';
import {
  BicBadgeComponent,
  BicButtonComponent,
  BicInputComponent,
} from '@buildinginformationcloud/bic-ui-components';

import {
  CatalogService,
  CatalogTargetOrganisation,
  CatalogTargets,
  CatalogTargetUser,
} from '../../../../services/catalog.service';
import {serverErrorMessage} from '../server-error-message';

/**
 * Zielgruppe beim zentralen Bereitstellen in Beta: global, an Organisationen und/oder an einzelne
 * Nutzer. Die Zielgruppe für Live legt das Admin Center bei der Übernahme fest – Organisationen und
 * Nutzer haben je Stage andere IDs.
 *
 * <p>Nutzer werden über ihre <b>E-Mail-Adresse</b> aufgelöst, nicht aus einer Liste gewählt: es gibt
 * bewusst keinen Endpunkt, der Nutzer auflistet.
 */
@Component({
  selector: 'app-catalog-targets-picker',
  standalone: true,
  imports: [FormsModule, FaIconComponent, BicBadgeComponent, BicButtonComponent, BicInputComponent],
  changeDetection: ChangeDetectionStrategy.Eager,
  template: `
    <div class="flex flex-col gap-4">
      <label class="flex flex-row items-start gap-3 rounded-lg border border-gray-200 p-3 cursor-pointer hover:border-green">
        <input (ngModelChange)="global = $event; emit()" [ngModel]="global" [disabled]="disabled"
               class="mt-0.5 h-4 w-4 rounded border-gray-300 text-green accent-green focus:ring-green"
               type="checkbox"/>
        <span class="flex flex-col">
          <span class="text-sm font-medium text-gray-900">Global bereitstellen (alle Organisationen)</span>
          <span class="text-xs text-gray-500">Für alle Kunden dieser Stage sichtbar.</span>
        </span>
      </label>

      <!-- Gezielte Ziele. Bei globaler Freigabe sichtbar deaktiviert, die Auswahl bleibt aber
           erhalten, damit ein versehentliches Häkchen sie nicht verwirft. -->
      <div [class.opacity-50]="global" class="flex flex-col gap-4">
        <div class="flex flex-col gap-2">
          <span class="text-sm font-medium text-gray-900">Organisationen</span>
          @if (selectedOrganisations.length > 0) {
            <div class="flex flex-row flex-wrap gap-2">
              @for (organisation of selectedOrganisations; track organisation.id) {
                <span class="flex flex-row items-center gap-1">
                  <bic-badge bgClass="bg-gray-100" textClass="text-gray-700">{{ organisation.name }}</bic-badge>
                  <button (click)="removeOrganisation(organisation)" [disabled]="disabled || global"
                          class="text-gray-400 hover:text-red disabled:cursor-not-allowed" type="button">
                    <fa-icon [icon]="faTrash" class="text-xs"></fa-icon>
                  </button>
                </span>
              }
            </div>
          }
          <bic-input (valueChange)="onOrganisationQueryChange($event)"
                     [disabled]="disabled || global"
                     [placeholder]="'Organisation suchen…'"
                     [value]="organisationQuery"></bic-input>
          @if (organisationError) {
            <span class="text-xs text-red">{{ organisationError }}</span>
          }
          @if (selectableOrganisations.length > 0 && !global) {
            <div class="flex flex-col max-h-[140px] overflow-y-auto border border-gray-200 rounded-lg">
              @for (organisation of selectableOrganisations; track organisation.id) {
                <button (click)="addOrganisation(organisation)" [disabled]="disabled"
                        class="text-left px-3 py-2 text-sm text-gray-900 hover:bg-gray-100 disabled:cursor-not-allowed"
                        type="button">
                  {{ organisation.name }}
                </button>
              }
            </div>
          }
        </div>

        <div class="flex flex-col gap-2">
          <span class="text-sm font-medium text-gray-900">Nutzer</span>
          @if (selectedUsers.length > 0) {
            <div class="flex flex-row flex-wrap gap-2">
              @for (user of selectedUsers; track user.id) {
                <span class="flex flex-row items-center gap-1">
                  <bic-badge bgClass="bg-gray-100" textClass="text-gray-700">{{ user.displayName }}</bic-badge>
                  <button (click)="removeUser(user)" [disabled]="disabled || global"
                          class="text-gray-400 hover:text-red disabled:cursor-not-allowed" type="button">
                    <fa-icon [icon]="faTrash" class="text-xs"></fa-icon>
                  </button>
                </span>
              }
            </div>
          }
          <div class="flex flex-row items-end gap-2">
            <bic-input (keyup.enter)="addUser()" (valueChange)="userMail = $event"
                       [disabled]="disabled || global" [placeholder]="'E-Mail-Adresse eingeben'"
                       [value]="userMail" class="flex-1"></bic-input>
            <bic-button (onClick)="addUser()" [disabled]="disabled || global || !userMail.trim()"
                        [loading]="lookingUpUser" buttonType="white">
              Hinzufügen
            </bic-button>
          </div>
          @if (userLookupError) {
            <span class="text-xs text-red">{{ userLookupError }}</span>
          }
        </div>
      </div>
    </div>
  `,
})
export class CatalogTargetsPickerComponent implements OnInit {
  @Input() disabled = false;
  /** Gibt die Zielgruppe bei jeder Änderung aus; {@code null}, solange sie leer ist. */
  @Output() targetsChange = new EventEmitter<CatalogTargets | null>();

  protected readonly faTrash = faTrash;

  protected global = false;
  protected organisations: CatalogTargetOrganisation[] = [];
  protected selectedOrganisations: CatalogTargetOrganisation[] = [];
  protected selectedUsers: CatalogTargetUser[] = [];
  protected organisationQuery = '';
  protected organisationError = '';
  protected userMail = '';
  protected lookingUpUser = false;
  protected userLookupError = '';

  private catalogService = inject(CatalogService);
  private destroyRef = inject(DestroyRef);
  private organisationSearch$ = new Subject<string>();

  protected get selectableOrganisations(): CatalogTargetOrganisation[] {
    const chosen = new Set(this.selectedOrganisations.map(entry => entry.id));
    return this.organisations.filter(entry => !chosen.has(entry.id));
  }

  ngOnInit(): void {
    this.organisationSearch$
      .pipe(
        debounceTime(250),
        switchMap(query => this.catalogService.lookupOrganisations(query)),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe({
        next: result => {
          this.organisations = result;
          this.organisationError = '';
        },
        error: err => (this.organisationError = serverErrorMessage(err, 'Organisationen konnten nicht geladen werden.')),
      });
    this.organisationSearch$.next('');
  }

  protected onOrganisationQueryChange(query: string): void {
    this.organisationQuery = query ?? '';
    this.organisationSearch$.next(this.organisationQuery);
  }

  protected addOrganisation(organisation: CatalogTargetOrganisation): void {
    if (this.selectedOrganisations.some(entry => entry.id === organisation.id)) return;
    this.selectedOrganisations = [...this.selectedOrganisations, organisation];
    this.emit();
  }

  protected removeOrganisation(organisation: CatalogTargetOrganisation): void {
    this.selectedOrganisations = this.selectedOrganisations.filter(entry => entry.id !== organisation.id);
    this.emit();
  }

  protected addUser(): void {
    const mail = this.userMail.trim();
    if (!mail || this.lookingUpUser) return;
    this.userLookupError = '';
    this.lookingUpUser = true;
    this.catalogService.lookupUser(mail).subscribe({
      next: user => {
        this.lookingUpUser = false;
        if (!user) {
          this.userLookupError = 'Kein Nutzer mit dieser E-Mail-Adresse gefunden.';
          return;
        }
        if (!this.selectedUsers.some(entry => entry.id === user.id)) {
          this.selectedUsers = [...this.selectedUsers, user];
          this.emit();
        }
        this.userMail = '';
      },
      error: err => {
        this.lookingUpUser = false;
        this.userLookupError = serverErrorMessage(err, 'Nutzer konnte nicht gesucht werden.');
      },
    });
  }

  protected removeUser(user: CatalogTargetUser): void {
    this.selectedUsers = this.selectedUsers.filter(entry => entry.id !== user.id);
    this.emit();
  }

  protected emit(): void {
    const targets: CatalogTargets = {
      global: this.global,
      organisationIds: this.selectedOrganisations.map(entry => entry.id),
      userIds: this.selectedUsers.map(entry => entry.id),
    };
    const empty = !targets.global && !targets.organisationIds.length && !targets.userIds.length;
    this.targetsChange.emit(empty ? null : targets);
  }
}
